import { createSelector, createReducer } from '@reduxjs/toolkit';
import { all, takeEvery, call, fork } from 'redux-saga/effects';
import { HearingStatusConnector } from '../connectors/hearingStatusConnector';
import { getAttr, getEntityId, getRelId } from '../helpers/apiHelpers';
import { generateGetAllApiCall } from '../helpers/sagaHelpers';
import {
  selectAllFromApiState,
  getIncludedData,
  getRequestData,
} from '../helpers/selectorHelpers';
import { updateDtoList } from '../helpers/reducerHelpers';
import { HEARINGSTATUS_INCLUDE } from '../../constants/api';
import {
  getAllHearingsSuccess,
  getAllHearingStatusInitialize,
  getAllHearingStatusSuccess,
  getHearingSuccess,
  getPaginatedHearingsSuccess,
} from '../actions/api';
import { hearingByIdSelector, hearingsByIdsSelector } from './hearingSlice';
import { activePageSelector } from '../componentFeatures/hearingOverviewSlice';
import { pageFilterSelections } from '../../utilities/pageFilterSelections';
import { filterAndSortingKeys } from '../../utilities/filterAndSortingsUtil';

// -------- Overview --------

// HearingStatus is included in the following endpoints
//  - GET /hearing
//  - GET /hearing/{hearingId}

// -------- Reducer --------
const hearingStatusReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(getAllHearingStatusSuccess, (state, action) => {
        const hearingStatus = getRequestData(action);
        return updateDtoList(state, 'all', hearingStatus);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingStatus = data.filter(
          (include) => include.type === HEARINGSTATUS_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingStatus);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingStatus = data.filter(
          (include) => include.type === HEARINGSTATUS_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingStatus);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const hearingStatus = data.filter(
          (include) => include.type === HEARINGSTATUS_INCLUDE,
        );
        return updateDtoList(state, 'all', hearingStatus);
      });
  },
);

// --------- Sagas ----------
export function* hearingStatusSaga() {
  yield all([fork(getAllHearingStatusSaga)]);
}

function* getAllHearingStatusSaga() {
  yield takeEvery(
    getAllHearingStatusInitialize.type,
    generateGetAllApiCall(HearingStatusConnector, function* (connector) {
      return yield call([connector, connector.getHearingStatuses]);
    }),
  );
}

// -------- Selectors --------
const selectGetAllHearingStatus = (state) =>
  selectAllFromApiState(state, 'hearingStatus');
const allHearingStatusSelector = createSelector(
  selectGetAllHearingStatus,
  (hearingStatus) => hearingStatus,
);
const hearingStatusByIdSelector = (hearingStatusId) =>
  createSelector([allHearingStatusSelector], (hearingStatus) =>
    hearingStatus.find((s) => getEntityId(s) === hearingStatusId),
  );
const hearingStatusByStatusSelector = (status) =>
  createSelector([allHearingStatusSelector], (hearingStatus) =>
    hearingStatus.find((s) => getAttr(s, 'status') === status),
  );

const hearingStatusesByHearingIdsSelector = (hearingIds) =>
  createSelector(
    [allHearingStatusSelector, hearingsByIdsSelector(hearingIds)],
    (hearingStatuses, hearings) =>
      hearingStatuses.filter((hearingStatus) =>
        hearings.some(
          (hearing) =>
            getRelId(hearing, 'hearingStatus') === getEntityId(hearingStatus),
        ),
      ),
  );

const hearingStatusByHearingIdSelector = (hearingId) =>
  createSelector(
    [allHearingStatusSelector, hearingByIdSelector(hearingId)],
    (hearingStatuses, hearing) =>
      hearingStatuses.find(
        (hearingStatus) =>
          getRelId(hearing, 'hearingStatus') === getEntityId(hearingStatus),
      ),
  );

const hearingStatusForActivePageSelector = createSelector(
  [allHearingStatusSelector, activePageSelector],
  (hearingStatuses, activePage) => {
    const pageSelection =
      pageFilterSelections[activePage] &&
      pageFilterSelections[activePage][filterAndSortingKeys.hearingStatus];

    if (pageSelection === undefined) {
      return hearingStatuses;
    }
    return hearingStatuses.filter((s) =>
      pageSelection.includes(getAttr(s, 'status')),
    );
  },
);

// -------- Exporting --------
export {
  allHearingStatusSelector,
  hearingStatusByIdSelector,
  hearingStatusByStatusSelector,
  hearingStatusesByHearingIdsSelector,
  hearingStatusByHearingIdSelector,
  hearingStatusForActivePageSelector,
};
export default hearingStatusReducer;
